'use client';

import { useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Analytics from '@/lib/analytics/posthog';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/zustand-stores/userStore';
import { cn } from '@/lib/utils';
import { HorizontalLogo } from '@/components/ui/horizontalLogo';
import Squares from '@/components/react-bits/SquaresBackground';
import { Input, MagneticButton, loginFormSchema, loginAnimationStyles } from './loginUtils';

export default function LoginForm() {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const { fetchUser } = useUserStore();
  const [cardHovered, setCardHovered] = useState(false);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  const signInForm = useForm<z.infer<typeof loginFormSchema>>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Focus email input on mount and handle autofill
  useEffect(() => {
    // Focus the email input and handle both inputs' states
    const handleInputStates = () => {
      // Handle email input
      if (emailInputRef.current) {
        emailInputRef.current.focus();
        if (emailInputRef.current.value) {
          signInForm.setValue('email', emailInputRef.current.value);
          setTimeout(() => emailInputRef.current?.blur(), 10);
        }
      }

      // Handle password input
      if (passwordInputRef.current?.value) {
        signInForm.setValue('password', passwordInputRef.current.value);
        // Trigger focus/blur to activate floating label
        setTimeout(() => {
          if (passwordInputRef.current) {
            passwordInputRef.current.focus();
            setTimeout(() => passwordInputRef.current?.blur(), 10);
          }
        }, 20);
      }
    };

    // Run immediately and after a short delay to catch autofill
    handleInputStates();
    const timeouts = [100, 300, 500].map(delay => setTimeout(handleInputStates, delay));

    return () => timeouts.forEach(clearTimeout);
  }, [signInForm]);

  const onSubmit = async (data: z.infer<typeof loginFormSchema>) => {
    try {
      setIsLoading(true);

      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error?.message?.length) {
        // Track failed login
        Analytics.trackLoginFailed({
          email: data.email,
          error_type: error.message.toLowerCase().includes('credentials') ? 'invalid_credentials' : 'other',
          error_message: error.message,
        });

        toast.error(error.message);
        return;
      }

      if (authData.user) {
        const { error: updateError } = await supabase
          .from('appUsers')
          .update({
            last_login: new Date().toISOString(),
            last_active: new Date().toISOString(),
          })
          .eq('id', authData.user.id);

        if (updateError) {
          console.error('Error updating login timestamp:', updateError);
        }

        await fetchUser();

        // Track successful login
        Analytics.trackLogin({
          user_id: authData.user.id,
          email: authData.user.email || data.email,
          success: true,
        });

        toast.success('Logged in successfully!');
        router.push('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative bg-white overflow-hidden">
      <Squares speed={0.1} squareSize={60} direction="down" borderColor="#e3e3e3" hoverFillColor="rgba(0, 0, 0, 100)" />

      <Form {...signInForm}>
        <form
          onSubmit={signInForm.handleSubmit(onSubmit)}
          className={cn(
            'relative z-10 bg-white w-full h-full sm:h-auto',
            'flex flex-col',
            'p-6 sm:p-6 md:p-8 lg:p-10',
            'sm:w-[440px] md:w-[480px] lg:w-[520px]',
            'sm:rounded-3xl',
            'mx-auto',
            'space-y-8 sm:space-y-10',
            'sm:border border-[#E5E7EB]',
            'transition-all duration-500 ease-out',
            cardHovered ? 'sm:shadow-lg' : 'sm:shadow-md'
          )}
          onMouseEnter={() => setCardHovered(true)}
          onMouseLeave={() => setCardHovered(false)}
        >
          {/* Logo and header */}
          <div className="text-center space-y-4 sm:space-y-6 pt-20 sm:pt-0">
            <HorizontalLogo className="h-12 sm:h-14 md:h-16 w-auto mx-auto" />
            <div className="space-y-2">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-foreground animate-fade-in">Welcome Back</h1>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground animate-fade-in-delay">Sign in to your account to continue</p>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-10">
            {/* Email input */}
            <div className="animate-slide-up">
              <FormField
                control={signInForm.control}
                name="email"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        label="Email"
                        type="email"
                        error={!!fieldState.error}
                        autoFocus
                        {...field}
                        ref={e => {
                          field.ref(e);
                          emailInputRef.current = e;
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Password input */}
            <div className="animate-slide-up-delay">
              <FormField
                control={signInForm.control}
                name="password"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="relative">
                      <Input
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        error={!!fieldState.error}
                        {...field}
                        ref={e => {
                          field.ref(e);
                          passwordInputRef.current = e;
                        }}
                        className="text-foreground"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-[34px] hover:bg-transparent z-20"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                        <span className="sr-only">{showPassword ? 'Hide password' : 'Show password'}</span>
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="animate-slide-up-delay-2">
            <MagneticButton type="submit" loading={isLoading}>
              Sign in
            </MagneticButton>
          </div>
        </form>
      </Form>

      <style jsx global>
        {loginAnimationStyles}
      </style>
    </div>
  );
}
